import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "file", "comments", "documentName", "documentType", "uploadButton", "dnaCheckbox", "documentCard", "fileInfo"]

  static values = {
    documentsUrl: String,       // Base URL for documents, e.g., /accounts/tax-organizers/2025/documents
    uploadDocumentUrl: String   // URL for uploading documents, e.g., /accounts/tax-organizers/2025/upload-document
  }

  MAX_FILES = 2

  connect() {
    this.documentUploadModal = null
    this.currentDocumentId = null
    this.currentCard = null
    this.dragCounters = new WeakMap()

    // Initialize Bootstrap modal
    const modalElement = document.getElementById('documentUploadModal')
    if (modalElement) {
      this.documentUploadModal = new bootstrap.Modal(modalElement)
    }
  }

  // Drag-and-drop handlers - called via data-action on document cards
  handleDragEnter(event) {
    event.preventDefault()
    const card = event.currentTarget
    const count = (this.dragCounters.get(card) || 0) + 1
    this.dragCounters.set(card, count)

    if (count === 1) {
      card.classList.add("drag-over")
    }
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  handleDragLeave(event) {
    event.preventDefault()
    const card = event.currentTarget
    const count = (this.dragCounters.get(card) || 1) - 1
    this.dragCounters.set(card, count)

    if (count === 0) {
      card.classList.remove("drag-over")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    const card = event.currentTarget
    this.dragCounters.set(card, 0)
    card.classList.remove("drag-over")

    const files = event.dataTransfer.files
    if (!files || files.length === 0) return

    if (files.length > this.MAX_FILES) {
      alert(`You can only upload a maximum of ${this.MAX_FILES} files at once.`)
      return
    }

    // Get document info from the upload button in this card
    const uploadButton = card.querySelector("[data-action*='openDocumentUpload']")
    if (!uploadButton) return

    const documentId = uploadButton.dataset.documentId

    // Upload the dropped files directly
    this.uploadDroppedFiles(files, documentId, card)
  }

  uploadDroppedFiles(files, documentId, card) {
    const formData = new FormData()

    Array.from(files).forEach(file => {
      formData.append('files[]', file)
    })
    formData.append('comments', '')
    formData.append('document_id', documentId)

    // Show loading state on card
    card.style.opacity = '0.6'
    card.style.pointerEvents = 'none'

    fetch(this.uploadDocumentUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Turbo-Frame': `document_${documentId}`,
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => {
      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        throw new Error('Permission denied')
      }
      return response.text()
    })
    .then(text => {
      Turbo.renderStreamMessage(text)

      this.dispatch('uploaded', {
        detail: {
          documentId: documentId,
          fileNames: Array.from(files).map(f => f.name)
        },
        bubbles: true
      })
    })
    .catch(error => {
      console.error('Error uploading document:', error)
      alert('Error uploading document. Please try again.')
    })
    .finally(() => {
      card.style.opacity = ''
      card.style.pointerEvents = ''
    })
  }

  disconnect() {
    if (this.documentUploadModal) {
      this.documentUploadModal.dispose()
    }
  }

  // Open document upload modal
  openDocumentUpload(event) {
    // Clear the form before opening
    this.clearForm()

    // Set the current document ID after clearing
    this.currentDocumentId = event.currentTarget.dataset.documentId

    // Get document context from button's data attributes
    const documentName = event.currentTarget.dataset.documentName
    const documentType = event.currentTarget.dataset.documentType

    // Update modal content with document context
    if (this.hasDocumentNameTarget && documentName) {
      this.documentNameTarget.textContent = documentName
    }
    if (this.hasDocumentTypeTarget && documentType) {
      this.documentTypeTarget.textContent = documentType
    }

    // Find modal by ID and initialize if needed
    const modalElement = document.getElementById('documentUploadModal')
    if (modalElement) {
      if (!this.documentUploadModal) {
        this.documentUploadModal = new bootstrap.Modal(modalElement)
      }
      this.documentUploadModal.show()
    }
  }

  // Upload document
  uploadDocument(event) {
    event.preventDefault()

    const formData = new FormData()
    const files = this.fileTarget.files
    const comments = this.hasCommentsTarget ? this.commentsTarget.value : ''

    if (!files || files.length === 0) {
      alert('Please select at least one file')
      return
    }

    // Validate file count again before upload
    if (files.length > this.MAX_FILES) {
      alert(`You can only upload a maximum of ${this.MAX_FILES} files at once.`)
      return
    }

    // Show loading state
    const uploadButton = event.currentTarget
    const originalText = uploadButton.textContent
    uploadButton.disabled = true
    uploadButton.textContent = 'Uploading...'

    // Append all files to FormData
    Array.from(files).forEach(file => {
      formData.append('files[]', file)
    })
    formData.append('comments', comments)
    formData.append('document_id', this.currentDocumentId)

    // POST /accounts/tax-organizers/:tax_year/upload-document
    // Send turbo_stream format and include the turbo frame header
    fetch(this.uploadDocumentUrlValue, {
      method: 'POST',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Turbo-Frame': `document_${this.currentDocumentId}`,
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => {
      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        throw new Error('Permission denied')
      }
      return response.text()
    })
    .then(text => {
      // Close modal on success
      if (this.documentUploadModal) {
        this.documentUploadModal.hide()
      }

      // Let Turbo handle the stream response - it will update the document card
      Turbo.renderStreamMessage(text)

      // Dispatch success event
      this.dispatch('uploaded', {
        detail: {
          documentId: this.currentDocumentId,
          fileNames: Array.from(files).map(f => f.name)
        },
        bubbles: true
      })

      // Clear the form
      this.clearForm()
    })
    .catch(error => {
      console.error('Error uploading document:', error)
      alert('Error uploading document. Please try again.')
    })
    .finally(() => {
      // Reset button state
      uploadButton.disabled = false
      uploadButton.textContent = originalText
    })
  }

  // Update document status (does not apply checkbox)
  updateDocumentStatus(event) {
    // Store reference to checkbox before async operations (event.currentTarget becomes null)
    const checkbox = event.currentTarget
    const documentId = checkbox.dataset.documentId
    const doesNotApply = checkbox.checked
    const hasUploads = checkbox.dataset.hasUploads === 'true'

    // If checking DNA and there are uploaded documents, confirm first
    if (doesNotApply && hasUploads) {
      const confirmed = confirm('This document has uploaded files. Marking it as "Does Not Apply" will clear any uploaded documents. Are you sure?')
      if (!confirmed) {
        // Revert the checkbox
        checkbox.checked = false
        return
      }
    }

    // PATCH /accounts/tax-organizers/:tax_year/documents/:id
    // Use turbo_stream format
    const formData = new FormData()
    formData.append('document[does_not_apply]', doesNotApply ? '1' : '0')

    fetch(`${this.documentsUrlValue}/${documentId}`, {
      method: 'PATCH',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Turbo-Frame': `document_${documentId}`,
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => {
      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        // Revert the checkbox
        checkbox.checked = !doesNotApply
        throw new Error('Permission denied')
      }
      return response.text()
    })
    .then(text => {
      // Let Turbo handle the stream response - it will update the document card
      Turbo.renderStreamMessage(text)

      // Dispatch status change event
      this.dispatch('status-changed', {
        detail: { documentId, doesNotApply },
        bubbles: true
      })
    })
    .catch(error => {
      console.error('Error updating document status:', error)
      alert('Error updating document status. Please try again.')
      // Revert the checkbox
      checkbox.checked = !doesNotApply
    })
  }

  // Delete individual file without page reload (used in document cards)
  deleteFile(event) {
    const button = event.currentTarget
    const fileId = button.dataset.fileId
    const documentId = button.dataset.documentId
    const deleteUrl = button.dataset.deleteUrl

    if (!confirm('Are you sure you want to delete this file?')) {
      return
    }

    // Disable button during deletion
    button.disabled = true

    // DELETE request to the specific file (request turbo_stream response)
    fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'Turbo-Frame': `document_${documentId}`,
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      }
    })
    .then(response => {
      if (response.status === 403) {
        alert('You do not have permission to make changes to this tax organizer.')
        throw new Error('Permission denied')
      }
      return response.text()
    })
    .then(text => {
      // Let Turbo handle the stream response - it will update all the frames
      Turbo.renderStreamMessage(text)

      // Dispatch deletion event
      this.dispatch('file-deleted', {
        detail: { fileId, documentId },
        bubbles: true
      })
    })
    .catch(error => {
      console.error('Error deleting file:', error)
      alert('Error deleting file. Please try again.')
      button.disabled = false
    })
  }

  // Clear document upload form
  clearForm() {
    if (this.hasFileTarget) {
      this.fileTarget.value = ''
    }
    if (this.hasCommentsTarget) {
      this.commentsTarget.value = ''
    }
    if (this.hasFileInfoTarget) {
      this.fileInfoTarget.style.display = 'none'
    }
    this.currentDocumentId = null
  }

  // Display file metadata using File API
  displayFileInfo(event) {
    const files = event.target.files

    if (!files || files.length === 0) {
      if (this.hasFileInfoTarget) {
        this.fileInfoTarget.style.display = 'none'
        this.fileInfoTarget.innerHTML = ''
      }
      return
    }

    // Validate file count - maximum 2 files
    if (files.length > this.MAX_FILES) {
      alert(`You can only upload a maximum of ${this.MAX_FILES} files at once. Please select ${this.MAX_FILES} or fewer files.`)
      event.target.value = ''
      if (this.hasFileInfoTarget) {
        this.fileInfoTarget.style.display = 'none'
        this.fileInfoTarget.innerHTML = ''
      }
      return
    }

    // Format file size to human-readable format
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    // Build file info HTML for each file
    let fileInfoHTML = ''
    Array.from(files).forEach((file, index) => {
      if (index > 0) {
        fileInfoHTML += '<div class="mt-1"></div>'
      }
      fileInfoHTML += `
        <small class="text-muted">
          <i class="fas fa-file me-1"></i>
          <span>${file.name}</span>
          <i class="fas fa-circle fa-2xs mx-2"></i>
          <span>${formatFileSize(file.size)}</span>
          <i class="fas fa-circle fa-2xs mx-2"></i>
          <span>${file.type || 'Unknown type'}</span>
        </small>
      `
    })

    // Display file metadata
    if (this.hasFileInfoTarget) {
      this.fileInfoTarget.innerHTML = fileInfoHTML
      this.fileInfoTarget.style.display = 'block'
    }
  }
}
